/**
 * What the driver FOUND at a property: a note per location, and reference photos
 * (delivery/driver_findings.py). Brad, 2026-09-14: "customer said" and "found" sit
 * side by side and never overwrite; the stop screen shows the finding first; a
 * driver's photo is labelled "added by your driver" for the customer.
 *
 * WRITE FIRST, like every stop write. saveFoundNote() / saveFoundPhoto() put the
 * record in IndexedDB, merge a note into the stop's cached access facts (so the stop
 * screen shows it with no signal), then ask for a drain. The drain uploads findings
 * in capture order, independent of the stop's completion: a finding is useful on a
 * skipped stop too. The record's id is the client_write_id the server dedupes on.
 */
import type { StopAccess } from './access'
import { getAccess, listFindingsForStop, putAccess, putFinding } from './db'
import { drain, newId, requestBackgroundSync } from './engine'
import type { FindingRecord } from './types'

/** The locations a driver can note, in the order they walk them. */
export const FOUND_LOCATIONS: { key: string; label: string; photoKind: string }[] = [
  { key: 'gate', label: 'Gate', photoKind: 'gate' },
  { key: 'latch', label: 'Latch', photoKind: 'latch' },
  { key: 'storage', label: 'Feed storage', photoKind: 'storage' },
  { key: 'feeder', label: 'Feeder', photoKind: 'feeder' },
  { key: 'waterer', label: 'Waterer', photoKind: 'waterer' },
  { key: 'grit', label: 'Grit', photoKind: 'grit' },
  { key: 'shell', label: 'Oyster shell', photoKind: 'shell' },
  { key: 'dog', label: 'Dog', photoKind: 'dog' },
  { key: 'notes', label: 'Anything else', photoKind: 'other' },
]

/** Which found-note location belongs to each customer access field. */
export const FIELD_TO_FOUND: Record<string, string> = {
  gate_location: 'gate',
  latch_notes: 'latch',
  feed_storage_location: 'storage',
  feeder_location: 'feeder',
  waterer_location: 'waterer',
  grit_location: 'grit',
  shell_location: 'shell',
  dog_notes: 'dog',
  notes: 'notes',
}

function kick(): void {
  void requestBackgroundSync()
  void drain({ source: 'foreground' })
}

function newRecord(stopUuid: string, fields: Partial<FindingRecord>): FindingRecord {
  const now = Date.now()
  return {
    id: newId(),
    stopUuid,
    kind: 'note',
    location: '',
    text: '',
    photoKind: '',
    blob: null,
    capturedAt: new Date(now).toISOString(),
    state: 'queued',
    attempts: 0,
    nextAttemptAt: now,
    lastError: '',
    createdAt: now,
    syncedAt: null,
    ...fields,
  }
}

/** Save a note for a location (blank clears the driver's finding). */
export async function saveFoundNote(stopUuid: string, location: string, text: string): Promise<FindingRecord> {
  const rec = newRecord(stopUuid, { kind: 'note', location, text })
  await putFinding(rec)
  // Show it at once, offline: merge into the cached access facts.
  const cached = await getAccess<StopAccess>(stopUuid)
  if (cached?.access) {
    const found = { ...(cached.access.found || {}) }
    if (text.trim()) found[location] = { text: text.trim(), by: 'You', at: rec.capturedAt }
    else delete found[location]
    await putAccess(stopUuid, { ...cached.access, found })
  }
  kick()
  return rec
}

/** Save a compressed reference photo of where something is. */
export async function saveFoundPhoto(stopUuid: string, photoKind: string, blob: Blob): Promise<FindingRecord> {
  const rec = newRecord(stopUuid, { kind: 'photo', photoKind, blob })
  await putFinding(rec)
  kick()
  return rec
}

/** Photos taken here that have not reached the server yet. */
export async function pendingFoundPhotos(stopUuid: string): Promise<FindingRecord[]> {
  return (await listFindingsForStop(stopUuid)).filter((f) => f.kind === 'photo' && f.state !== 'synced')
}
